import { countryCodeFromIDOrCode } from "Utilities/Address/networkCountryOptions";
import appendToFormData from "Utilities/appendToFormData";

/**
 * Serialize an address value onto a FormData for the
 * saveExtendedFieldResponse(IncludingOtherNetworks) endpoints.
 *
 * The verification encoding here is the canonical logic from
 * `src/ExtendedFields/API/queryHooks.js` (saveField's `case "address"`,
 * ~lines 132-198). Kept deliberately identical to that source — read it for the
 * full rationale behind each branch. This surface has no legacy verify-flow, so
 * the `options.verification` and `allowChangeCountry` branches of the original
 * are intentionally dropped; everything else matches.
 *
 * @param {FormData} formData
 * @param {object} address
 * @returns {FormData} the same formData, mutated
 */
export default function appendAddressToFormData(formData, address) {
  const cleaned = Object.entries(address).reduce((acc, [key, val]) => {
    if (val != null) acc[key] = val;
    return acc;
  }, {});

  // A non-US address carries NO verification verdict — the backend holds null
  // for every non-US address. Omit the verdict fields so the POST preserves
  // that null rather than manufacturing an "unverified" verdict the backend
  // never made.
  const isUSAddress =
    countryCodeFromIDOrCode(address?.country || address?.countryID) === "US";
  if (!isUSAddress) {
    delete cleaned.isVerified;
    delete cleaned.verificationProvider;
    delete cleaned.verifiedAt;
    appendToFormData(formData, cleaned);
    return formData;
  }

  let isVerified;
  if (cleaned.isVerified === true) {
    isVerified = true;
  } else {
    isVerified = false;
    delete cleaned.verificationProvider;
    delete cleaned.verifiedAt;
  }
  // FormData stringifies primitives, and PHP's `(bool)` reads the string
  // "false" as truthy — so encode as "1"/"0" on the wire. The in-memory
  // contract stays boolean everywhere else.
  cleaned.isVerified = isVerified ? "1" : "0";
  appendToFormData(formData, cleaned);
  return formData;
}
